import { CheckCircle2, AlertTriangle, Loader2, Clock } from "lucide-react";
import type { SaveStatus } from "@/pages/Index";
import type { UiTranslations } from "@/lib/ui-translations";

interface MilestoneProgressProps {
  currentStepIndex: number;
  totalSteps: number;
  /** Current section name */
  currentSection?: string;
  /** Index of current question within its section */
  stepInSection: number;
  /** Total questions in current section */
  totalInSection: number;
  /** Auto-save status from parent */
  saveStatus?: SaveStatus;
  /** UI translations */
  t: UiTranslations;
}

/**
 * Ease-out curve so early questions in a section feel faster.
 */
function perceivedProgress(linear: number): number {
  return Math.sqrt(linear) * 100;
}

function SaveIndicator({ status, t }: { status: SaveStatus; t: UiTranslations }) {
  if (status === 'idle') return null;

  if (status === 'saving') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground animate-pulse">
        <Loader2 className="w-3 h-3 animate-spin" />
        {t.saving}
      </span>
    );
  }

  if (status === 'saved') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-600 animate-in fade-in duration-300">
        <CheckCircle2 className="w-3 h-3" />
        {t.saved}
      </span>
    );
  }

  // error
  return (
    <span className="inline-flex items-center gap-1 text-xs text-destructive animate-in fade-in duration-300">
      <AlertTriangle className="w-3 h-3" />
      {t.saveError}
    </span>
  );
}

function getTimeRemaining(currentStep: number, totalSteps: number, t: UiTranslations): string {
  const stepsRemaining = totalSteps - currentStep;
  const minutesPerStep = 0.5;
  const minutesRemaining = Math.ceil(stepsRemaining * minutesPerStep);

  if (minutesRemaining <= 1) return t.lessThanMinute;
  return t.aboutMinutes(minutesRemaining);
}

export function MilestoneProgress({
  currentStepIndex,
  totalSteps,
  currentSection,
  stepInSection,
  totalInSection,
  saveStatus = 'idle',
  t,
}: MilestoneProgressProps) {
  const sectionLinear = Math.min((stepInSection + 1) / totalInSection, 1);
  const sectionProgress = perceivedProgress(sectionLinear);
  const overallProgress = Math.min(((currentStepIndex + 1) / totalSteps) * 100, 100);

  return (
    <div className="mb-6 animate-in fade-in duration-300">
      {/* Overall progress */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {t.questionOf(currentStepIndex + 1, totalSteps)}
          </span>
          {currentStepIndex < totalSteps - 1 && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground/70">
              <Clock className="w-3 h-3" />
              {getTimeRemaining(currentStepIndex, totalSteps, t)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <SaveIndicator status={saveStatus} t={t} />
          <span className="text-xs text-muted-foreground">
            {Math.round(overallProgress)}%
          </span>
        </div>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden mb-3">
        <div
          className="h-full bg-primary/30 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${overallProgress}%` }}
        />
      </div>

      {/* Section progress */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-primary">
          {currentSection || t.progress}
        </span>
        <span className="text-xs text-muted-foreground">
          {stepInSection + 1}/{totalInSection}
        </span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
          style={{ width: `${sectionProgress}%` }}
        />
      </div>
    </div>
  );
}
